import { Component, computed, effect, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
    suggestionUri,
    WorkSuggestionsListComponent,
} from '../../work/pages/work-suggestions-page/work-suggestions-list/work-suggestions-list.component';
import { WorkTypeEnum } from '@libs/types/src/common/enum/work-type.enum';
import { fb, routeParams, translate } from '../../../../modules/shared/shared.global-service';
import {
    WorkDataSuggestionTypeEnum,
    workDataSuggestionTypeEnumOptions,
} from '@libs/types/src/services/enum/work-data-suggestion-type.enum';
import {
    SelectButtonInputComponent,
} from '../../../kit/input/select-button-input/select-button-input.component';
import { ButtonComponent } from '../../../kit/button/button.component';
import { Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { FolderSuggestionsPageStore } from './folder-suggestions-page.store';

export function suggestionFolderUri(type: string, folderId: number | string) {
    return `/content/social/folders/${folderId}/suggestion?type=${type}`;
}

@Component({
    selector: 'app-folder-suggestions-page',
    templateUrl: './folder-suggestions-page.component.html',
    imports: [WorkSuggestionsListComponent, SelectButtonInputComponent, ButtonComponent],
    providers: [FolderSuggestionsPageStore],
})
export class FolderSuggestionsPageComponent {
    protected readonly router = inject(Router);
    protected readonly route = inject(ActivatedRoute);
    protected readonly location = inject(Location);
    public readonly store = inject(FolderSuggestionsPageStore);

    protected form = fb.group({
        type: fb.control<WorkDataSuggestionTypeEnum>(
            (this.route.snapshot.queryParams['type'] as WorkDataSuggestionTypeEnum) ?? WorkDataSuggestionTypeEnum.satisfaction,
        ),
    });

    protected readonly typeChange = toSignal(
        this.form.controls.type.valueChanges,
        { initialValue: this.form.controls.type.value },
    );

    private readonly counts = signal<Record<WorkDataSuggestionTypeEnum, number>>({
        [WorkDataSuggestionTypeEnum.satisfaction]: 0,
        [WorkDataSuggestionTypeEnum.complaint]: 0,
        [WorkDataSuggestionTypeEnum.finding]: 0,
    });

    protected suggestionTypes = computed(() => {
        const c = this.counts();
        return workDataSuggestionTypeEnumOptions.map((it) => ({
            key: `${translate.instant(it.name)} (${c[it.value as WorkDataSuggestionTypeEnum] ?? 0})`,
            value: it.value as string,
        }));
    });

    protected additionalFilter = computed(() => {
        const type = this.typeChange();
        return [
            [
                {
                    value: [this.store.globalGlobalStore.organizationStore.selectedId()],
                    column: 'organizationId',
                    operator: 'EQ',
                },
            ],
            [
                {
                    value: [WorkTypeEnum.SUGGESTION],
                    column: 'workType',
                    operator: 'EQ',
                },
            ],
            [
                {
                    value: [routeParams.folderId()],
                    column: 'folderId',
                    operator: 'IN',
                },
            ],
            ...(type ? [[
                {
                    value: [type],
                    column: 'workData.swdsg.type',
                    operator: 'EQ',
                },
            ]] : []),
        ];
    });

    constructor() {
        effect(() => {
            const type = this.typeChange();
            const urlTree = this.router.createUrlTree([], {
                relativeTo: this.route,
                queryParams: { type },
                queryParamsHandling: 'merge',
            });
            this.location.replaceState(this.router.serializeUrl(urlTree));
        });

        effect(async () => {
            const folderId = routeParams.folderId();
            const organizationId = this.store.globalGlobalStore.organizationStore.selectedId().toString();

            if (!folderId || folderId === '0') return;

            const baseFilter = [
                [{ value: [organizationId], column: 'organizationId', operator: 'EQ' }],
                [{ value: [WorkTypeEnum.SUGGESTION], column: 'workType', operator: 'EQ' }],
                [{ value: [folderId], column: 'folderId', operator: 'IN' }],
            ];

            const results = await Promise.all(
                Object.values(WorkDataSuggestionTypeEnum).map(async (type) => {
                    const res = await this.store.api.getWorks(
                        {
                            filter: [...baseFilter, [{ value: [type], column: 'workData.swdsg.type', operator: 'EQ' }]],
                            paging: { page: 1, itemsPerPage: 1 },
                        },
                        'id',
                    );
                    return [type, res?.paging?.total ?? 0] as [WorkDataSuggestionTypeEnum, number];
                }),
            );

            this.counts.set(Object.fromEntries(results) as Record<WorkDataSuggestionTypeEnum, number>);
        });
    }

    protected async create(type: WorkDataSuggestionTypeEnum): Promise<void> {
        const personId = this.store.personId();
        const uri = suggestionUri(type) + (personId ? `?personId=${personId}` : '');
        await this.router.navigateByUrl(uri);
    }

    protected readonly WorkDataSuggestionTypeEnum = WorkDataSuggestionTypeEnum;
}
